import {
  toCivilDate,
  ScheduleNoteRecurrenceType,
  isDefined,
  isCivilDate,
} from '@mfw/common';

import type {
  UiUpdateScheduleQuestionnaire,
  UiCreateScheduleQuestionnaire,
  UiQuestionnaireRepeatType,
  ApiScheduleQuestionnaire,
  UiScheduleQuestionnaire,
} from '../../schemas/scheduleQuestionnaire.schema';

/**
 * UI -> API
 */
const RECURRENCE_MAP: Record<
  UiQuestionnaireRepeatType,
  ScheduleNoteRecurrenceType
> = {
  custom: ScheduleNoteRecurrenceType.CUSTOM,
  every_n_days: ScheduleNoteRecurrenceType.EVERY_N_DAYS,
};

/**
 * API -> UI
 */
const MAP_RECURRENCE: Record<
  ScheduleNoteRecurrenceType,
  UiQuestionnaireRepeatType
> = {
  [ScheduleNoteRecurrenceType.CUSTOM]: 'custom',
  [ScheduleNoteRecurrenceType.EVERY_N_DAYS]: 'every_n_days',
};

export const createScheduleQuestionnaireApiArgs = (
  ui: UiCreateScheduleQuestionnaire,
) => {
  const payload: Record<string, unknown> = {
    clientId: ui.clientId,
    dateTime: ui.dateTime.toISOString(),
    // D1: the PICKED civil day rides with the instant
    businessDate: toCivilDate(ui.dateTime),
    questionnaireId: ui.questionnaireId,
    ...(isDefined(ui.deadlineDays) ? { deadlineDays: ui.deadlineDays } : {}),
    ...(ui.organizationId ? { organizationId: ui.organizationId } : {}),
  };

  if (ui.repeat) {
    payload.recurrenceType = RECURRENCE_MAP[ui.repeatType];

    if (ui.repeatType === 'every_n_days') {
      // an emptied number field holds null; the DTO takes a number or nothing
      if (isDefined(ui.repeatInterval)) {
        payload.recurrenceInterval = ui.repeatInterval;
      }

      if (isCivilDate(ui.repeatEndDate)) {
        // Civil end day, inclusive — validated passthrough; the form state
        // already carries 'yyyy-MM-dd' (FormCivilDatePicker).
        payload.recurrenceEndDate = ui.repeatEndDate;
      }
    }

    if (ui.repeatType === 'custom' && ui.customDates.length > 0) {
      // bare civil days — the picked days verbatim (dual-mode wire)
      payload.customDates = ui.customDates.map((d) => toCivilDate(d));
    }
  }

  return payload;
};

export const updateScheduleQuestionnaireApiArgs = (
  ui: UiUpdateScheduleQuestionnaire,
) => ({
  ...(ui.dateTime
    ? {
        dateTime: ui.dateTime.toISOString(),
        // D1: a moved instant carries its picked day
        businessDate: toCivilDate(ui.dateTime),
      }
    : {}),
  ...(ui.deadlineDays !== undefined ? { deadlineDays: ui.deadlineDays } : {}),
});

export const mapScheduleQuestionnaireFromApi = (
  scheduleQuestionnaire: ApiScheduleQuestionnaire,
): UiScheduleQuestionnaire => {
  return {
    ...scheduleQuestionnaire,
    recurrenceType: scheduleQuestionnaire.recurrenceType
      ? MAP_RECURRENCE[scheduleQuestionnaire.recurrenceType]
      : null,
  };
};
